import React, { useState, useMemo, useEffect } from 'react';
import { Card, Table, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import moment from 'moment';
import { LICHHUONGDANTHUCHANH_API } from '../../../../utils/apiUrl';
import { Get, Post } from '../../../../utils/httpProvider';

const InstructorAttendanceThucHanhTab = ({ lopId }) => {
    // 1. States & Filters
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [searchKeyword, setSearchKeyword] = useState('');
    const [attendanceList, setAttendanceList] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [activeNoteIndex, setActiveNoteIndex] = useState({});

    // 2. Fetch danh sách học viên của chính Giảng viên đang đăng nhập
    const fetchApiUrl = useMemo(() => {
        if (!lopId || !selectedDate) return '';
        return `${LICHHUONGDANTHUCHANH_API.GET_DIEM_DANH_GIANG_VIEN_CA_NHAN}/${lopId}?ngay=${selectedDate}`;
    }, [lopId, selectedDate]);

    const { data: sessionData, isLoading, refetch } = useQuery(
        ['diemDanhGiangVienSession', fetchApiUrl],
        () => Get(fetchApiUrl),
        {
            enabled: !!fetchApiUrl,
            staleTime: 0,
            cacheTime: 0,
            onSuccess: (res) => {
                const list = res?.danhSachHocVien?.$values || res?.danhSachHocVien || [];
                setAttendanceList(list);
                setIsDirty(false);
            }
        }
    );

    useEffect(() => {
        if (sessionData) {
            const list = sessionData?.danhSachHocVien?.$values || sessionData?.danhSachHocVien || [];
            setAttendanceList(list);
            setIsDirty(false);
        } else if (!fetchApiUrl) {
            setAttendanceList([]);
            setIsDirty(false);
        }
    }, [sessionData, fetchApiUrl]);

    // 3. Mutation Lưu điểm danh cho Giảng viên
    const saveBatchMutation = useMutation(
        (payload) => Post(LICHHUONGDANTHUCHANH_API.SAVE_DIEM_DANH_GIANG_VIEN, null, payload),
        {
            onSuccess: () => {
                toast.success("Lưu kết quả điểm danh thành công!");
                setIsDirty(false);
                refetch();
            },
            onError: (err) => toast.error(err?.message || "Lỗi khi lưu điểm danh!")
        }
    );

    const handleStatusChange = (index, status) => {
        const updated = [...attendanceList];
        updated[index].trangThai = Number(status);
        setAttendanceList(updated);
        setIsDirty(true);
    };

    const handleNoteChange = (index, note) => {
        const updated = [...attendanceList];
        updated[index].ghiChu = note;
        setAttendanceList(updated);
        setIsDirty(true);
    };

    // Nút chọn tất cả có mặt
    const handleSetAllPresent = () => {
        const updated = attendanceList.map(item => ({
            ...item,
            trangThai: item.isNgoaiLich ? 2 : 1
        }));
        setAttendanceList(updated);
        setIsDirty(true);
        toast.info("Đã đánh dấu tất cả học viên: Có mặt.");
    };

    const handleSaveAll = () => {
        const payload = {
            lopDaoTaoThucHanhId: lopId,
            ngayThucTe: selectedDate,
            items: attendanceList.map(item => ({
                diemDanhId: item.diemDanhId,
                hoSoTuyenSinhId: item.hoSoTuyenSinhId,
                lichGocId: item.lichHocId,
                lichThucTeId: item.lichHocId,
                trangThai: item.trangThai,
                ghiChu: item.ghiChu
            }))
        };
        saveBatchMutation.mutate(payload);
    };

    // Tìm kiếm theo tên học viên
    const filteredAttendanceList = useMemo(() => {
        if (!searchKeyword) return attendanceList;
        const kw = searchKeyword.toLowerCase().trim();
        return attendanceList.filter(item =>
            (item.hocVienTen && item.hocVienTen.toLowerCase().includes(kw)) ||
            (item.donVi && item.donVi.toLowerCase().includes(kw)) ||
            (item.maDonVi && item.maDonVi.toLowerCase().includes(kw))
        );
    }, [attendanceList, searchKeyword]);

    // Thống kê nhanh quân số
    const summary = useMemo(() => {
        let coMat = 0;
        let vangCP = 0;
        let vangKP = 0;
        let hocBu = 0;
        let chuaDiemDanh = 0;

        attendanceList.forEach(item => {
            if (item.trangThai === 1 || item.trangThai === 2) coMat++;
            else if (item.trangThai === 3) vangCP++;
            else if (item.trangThai === 4) vangKP++;
            else if (item.trangThai === 5) hocBu++;
            else chuaDiemDanh++;
        });

        return {
            coMat,
            vangCP,
            vangKP,
            hocBu,
            chuaDiemDanh,
            total: attendanceList.length
        };
    }, [attendanceList]);

    // Helper render 4 nút bấm trạng thái theo phong cách Pastel bo góc mềm mại
    const renderStatusButtons = (row, idx, isMobile = false) => {
        const currentSt = row.trangThai;
        const isCoMat = currentSt === 1 || currentSt === 2;
        const isVangCP = currentSt === 3;
        const isVangKP = currentSt === 4;
        const isHocBu = currentSt === 5;

        const baseBtnStyle = {
            borderRadius: '6px',
            padding: isMobile ? '7px 4px' : '5px 12px',
            fontSize: isMobile ? '12px' : '12px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            outline: 'none',
            flex: isMobile ? 1 : 'none',
            textAlign: 'center'
        };

        return (
            <div className={`d-flex align-items-center ${isMobile ? 'w-100 justify-content-between' : 'justify-content-center'}`} style={{ gap: isMobile ? '4px' : '6px' }}>
                {/* 1. CÓ MẶT */}
                <button
                    type="button"
                    style={{
                        ...baseBtnStyle,
                        backgroundColor: isCoMat ? '#e8f5e9' : '#ffffff',
                        border: isCoMat ? '1.5px solid #2e7d32' : '1px solid #cbd5e1',
                        color: isCoMat ? '#1b5e20' : '#475569',
                        fontWeight: isCoMat ? '700' : '500'
                    }}
                    onClick={() => handleStatusChange(idx, row.isNgoaiLich ? 2 : 1)}
                >
                    Có mặt
                </button>

                {/* 2. VẮNG CP */}
                <button
                    type="button"
                    style={{
                        ...baseBtnStyle,
                        backgroundColor: isVangCP ? '#fff8e1' : '#ffffff',
                        border: isVangCP ? '1.5px solid #f57f17' : '1px solid #cbd5e1',
                        color: isVangCP ? '#b76e00' : '#475569',
                        fontWeight: isVangCP ? '700' : '500'
                    }}
                    onClick={() => handleStatusChange(idx, 3)}
                >
                    Vắng CP
                </button>

                {/* 3. VẮNG KP */}
                <button
                    type="button"
                    style={{
                        ...baseBtnStyle,
                        backgroundColor: isVangKP ? '#ffebee' : '#ffffff',
                        border: isVangKP ? '1.5px solid #c62828' : '1px solid #cbd5e1',
                        color: isVangKP ? '#b71c1c' : '#475569',
                        fontWeight: isVangKP ? '700' : '500'
                    }}
                    onClick={() => handleStatusChange(idx, 4)}
                >
                    Vắng KP
                </button>

                {/* 4. HỌC BÙ */}
                <button
                    type="button"
                    style={{
                        ...baseBtnStyle,
                        backgroundColor: isHocBu ? '#e3f2fd' : '#ffffff',
                        border: isHocBu ? '1.5px solid #1976d2' : '1px solid #cbd5e1',
                        color: isHocBu ? '#0d47a1' : '#475569',
                        fontWeight: isHocBu ? '700' : '500'
                    }}
                    onClick={() => handleStatusChange(idx, 5)}
                >
                    Học bù
                </button>
            </div>
        );
    };

    return (
        <div>
            {/* CARD CHÍNH */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '12px' }}>
                {/* 1. HEADER TOOLBAR */}
                <Card.Header className="bg-white py-3 border-0" style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <Row className="align-items-center justify-content-between">
                        {/* Tiêu đề & Sĩ số */}
                        <Col lg={4} md={5} sm={12} className="mb-2 mb-md-0 d-flex align-items-center">
                            <h5 className="mb-0 font-weight-bold text-dark mr-2" style={{ color: '#1e293b' }}>
                                Điểm danh học viên
                            </h5>
                            <span
                                className="px-2 py-1 rounded-pill small font-weight-bold"
                                style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}
                            >
                                {summary.total} học viên
                            </span>
                        </Col>

                        {/* Bộ lọc ngày, tìm kiếm & Nút lưu Desktop */}
                        <Col lg={8} md={7} sm={12}>
                            <div className="d-flex flex-wrap align-items-center justify-content-md-end" style={{ gap: '8px' }}>
                                {/* Chọn Ngày */}
                                <div>
                                    <Form.Control
                                        size="sm"
                                        type="date"
                                        style={{ width: 145, borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                    />
                                </div>

                                {/* Tìm kiếm học viên */}
                                <div>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Tìm học viên..."
                                        style={{ width: 160, borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                        value={searchKeyword}
                                        onChange={(e) => setSearchKeyword(e.target.value)}
                                    />
                                </div>

                                {/* Nút Tất cả có mặt */}
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    style={{ borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '500' }}
                                    disabled={attendanceList.length === 0}
                                    onClick={handleSetAllPresent}
                                    title="Đánh dấu tất cả học viên có mặt"
                                >
                                    Tất cả có mặt
                                </Button>

                                {/* Nút Lưu điểm danh (Desktop) */}
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="d-none d-md-inline-block font-weight-bold shadow-sm"
                                    style={{
                                        backgroundColor: '#2563eb',
                                        borderColor: '#2563eb',
                                        borderRadius: '8px',
                                        padding: '6px 18px'
                                    }}
                                    disabled={attendanceList.length === 0 || saveBatchMutation.isLoading}
                                    onClick={handleSaveAll}
                                >
                                    {saveBatchMutation.isLoading ? "Đang lưu..." : "Lưu điểm danh"}
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* 2. THANH THỐNG KÊ QUÂN SỐ TỨC THÌ (SUMMARY PILLS) */}
                    <div className="d-flex flex-wrap align-items-center mt-3" style={{ gap: '8px' }}>
                        <span
                            className="px-3 py-1 rounded-pill small font-weight-bold"
                            style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }}
                        >
                            Có mặt {summary.coMat}
                        </span>
                        <span
                            className="px-3 py-1 rounded-pill small font-weight-bold"
                            style={{ backgroundColor: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082' }}
                        >
                            Vắng có phép {summary.vangCP}
                        </span>
                        <span
                            className="px-3 py-1 rounded-pill small font-weight-bold"
                            style={{ backgroundColor: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2' }}
                        >
                            Vắng không phép {summary.vangKP}
                        </span>
                        <span
                            className="px-3 py-1 rounded-pill small font-weight-bold"
                            style={{ backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb' }}
                        >
                            Học bù {summary.hocBu}
                        </span>
                        {summary.chuaDiemDanh > 0 && (
                            <span
                                className="px-3 py-1 rounded-pill small font-weight-bold"
                                style={{ backgroundColor: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}
                            >
                                Chưa điểm danh {summary.chuaDiemDanh}
                            </span>
                        )}
                        {isDirty && (
                            <span className="small text-warning font-weight-bold ml-auto d-none d-md-inline-block">
                                <i className="fas fa-circle mr-1" style={{ fontSize: '8px' }}></i> Thay đổi chưa lưu
                            </span>
                        )}
                    </div>
                </Card.Header>

                {/* 3. DESKTOP VIEW: BẢNG TABLE (HIỂN THỊ TRÊN MÀN HÌNH >= 768px) */}
                <Card.Body className="p-0 d-none d-md-block">
                    <div className="table-responsive">
                        <Table hover className="mb-0 text-nowrap align-middle" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                            <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <tr>
                                    <th style={{ width: 45, color: '#64748b', fontWeight: '600', fontSize: '13px' }} className="text-center">#</th>
                                    <th style={{ minWidth: 200, color: '#64748b', fontWeight: '600', fontSize: '13px' }}>Họ và tên học viên</th>
                                    <th style={{ minWidth: 160, color: '#64748b', fontWeight: '600', fontSize: '13px' }}>Khoa / Đơn vị</th>
                                    <th style={{ minWidth: 320, color: '#64748b', fontWeight: '600', fontSize: '13px' }} className="text-center">Trạng thái điểm danh</th>
                                    <th style={{ width: 120, color: '#64748b', fontWeight: '600', fontSize: '13px' }} className="text-center">Loại buổi</th>
                                    <th style={{ minWidth: 220, color: '#64748b', fontWeight: '600', fontSize: '13px' }}>Ghi chú</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-5">
                                            <span className="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                                            Đang tải danh sách học viên ngày {moment(selectedDate).format('DD/MM/YYYY')}...
                                        </td>
                                    </tr>
                                ) : filteredAttendanceList.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-5 text-muted">
                                            <i className="far fa-calendar-times fa-3x mb-3 text-secondary d-block" style={{ opacity: 0.35 }}></i>
                                            Không có lịch hướng dẫn học viên nào vào ngày {moment(selectedDate).format('DD/MM/YYYY')}.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredAttendanceList.map((row, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td className="text-center text-muted" style={{ fontSize: '13px' }}>{idx + 1}</td>
                                            <td className="font-weight-bold" style={{ color: '#1e293b', fontSize: '14px' }}>
                                                {row.hocVienTen}
                                            </td>
                                            <td style={{ color: '#64748b', fontSize: '13px' }}>
                                                {row.maDonVi || row.donVi || '—'}
                                            </td>
                                            <td className="text-center">
                                                {renderStatusButtons(row, idx, false)}
                                            </td>
                                            <td className="text-center">
                                                {row.isNgoaiLich ? (
                                                    <span
                                                        className="px-2 py-1 rounded-pill small"
                                                        style={{ backgroundColor: '#fefce8', color: '#ca8a04', border: '1px solid #fef08a' }}
                                                    >
                                                        Ngoài lịch
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="px-2 py-1 rounded-pill small font-weight-500"
                                                        style={{ backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #dcfce7' }}
                                                    >
                                                        Theo lịch
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="Thêm ghi chú..."
                                                    style={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                                    value={row.ghiChu || ""}
                                                    onChange={(e) => handleNoteChange(idx, e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Footer Desktop */}
                    {!isLoading && filteredAttendanceList.length > 0 && (
                        <div className="p-3 bg-white text-muted small border-top" style={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                            Đang hiển thị {filteredAttendanceList.length} / {attendanceList.length} học viên
                        </div>
                    )}
                </Card.Body>

                {/* 4. MOBILE / TABLET VIEW: CARD LIST (HIỂN THỊ TRÊN MÀN HÌNH < 768px) */}
                <div className="d-block d-md-none p-3 bg-light">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <span className="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                            Đang tải học viên...
                        </div>
                    ) : filteredAttendanceList.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="far fa-calendar-times fa-3x mb-3 text-secondary d-block" style={{ opacity: 0.35 }}></i>
                            Không có lịch học viên nào vào ngày {moment(selectedDate).format('DD/MM/YYYY')}.
                        </div>
                    ) : (
                        <div className="d-flex flex-column" style={{ gap: '12px', paddingBottom: '70px' }}>
                            {filteredAttendanceList.map((row, idx) => {
                                const hasNote = !!row.ghiChu;
                                const isNoteOpen = activeNoteIndex[idx] || hasNote;

                                return (
                                    <div
                                        key={idx}
                                        className="bg-white p-3 shadow-sm"
                                        style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                                    >
                                        {/* Card Header: Avatar tròn + Tên học viên + Khoa + Badge Loại buổi */}
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="mr-3 font-weight-bold"
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#e0f2fe',
                                                        color: '#0284c7',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    {idx + 1}
                                                </div>
                                                <div>
                                                    <div className="font-weight-bold" style={{ color: '#1e293b', fontSize: '15px' }}>
                                                        {row.hocVienTen}
                                                    </div>
                                                    <div style={{ color: '#64748b', fontSize: '13px' }}>
                                                        {row.maDonVi || row.donVi || '—'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                {row.isNgoaiLich ? (
                                                    <span
                                                        className="px-2 py-1 rounded-pill small"
                                                        style={{ backgroundColor: '#fefce8', color: '#ca8a04', border: '1px solid #fef08a', fontSize: '11px' }}
                                                    >
                                                        Ngoài lịch
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="px-2 py-1 rounded-pill small"
                                                        style={{ backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #dcfce7', fontSize: '11px' }}
                                                    >
                                                        Theo lịch
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Row 4 nút trạng thái mobile */}
                                        <div className="mb-2">
                                            {renderStatusButtons(row, idx, true)}
                                        </div>

                                        {/* Phần ghi chú Mobile */}
                                        <div className="mt-2">
                                            {isNoteOpen ? (
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="Nhập ghi chú..."
                                                    style={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                                    value={row.ghiChu || ""}
                                                    onChange={(e) => handleNoteChange(idx, e.target.value)}
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-decoration-none small"
                                                    style={{ color: '#0284c7', fontSize: '12px' }}
                                                    onClick={() => setActiveNoteIndex({ ...activeNoteIndex, [idx]: true })}
                                                >
                                                    + Thêm ghi chú
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Card>

            {/* 5. STICKY BOTTOM ACTION BAR CHO MOBILE (< 768px) */}
            <div
                className="d-block d-md-none position-fixed bg-white border-top shadow-lg p-3"
                style={{
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1040,
                    boxShadow: '0 -4px 15px rgba(0,0,0,0.06)'
                }}
            >
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <div className="small text-muted">
                            {summary.chuaDiemDanh > 0 ? (
                                <span>{summary.chuaDiemDanh} học viên chưa điểm danh</span>
                            ) : (
                                <span className="text-success font-weight-bold">Đã điểm danh đủ</span>
                            )}
                        </div>
                        {isDirty && (
                            <div className="small text-warning font-weight-bold">
                                Thay đổi chưa lưu
                            </div>
                        )}
                    </div>

                    <Button
                        variant="primary"
                        className="font-weight-bold px-4 py-2"
                        style={{
                            backgroundColor: '#2563eb',
                            borderColor: '#2563eb',
                            borderRadius: '8px'
                        }}
                        disabled={attendanceList.length === 0 || saveBatchMutation.isLoading}
                        onClick={handleSaveAll}
                    >
                        {saveBatchMutation.isLoading ? "Đang lưu..." : "Lưu điểm danh"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default InstructorAttendanceThucHanhTab;
